import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Heart, Play, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ProductReviews from '../components/ProductReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = ({ setCurrentAudio }) => {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInWishlist, setIsInWishlist] = useState(false);
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchProduct();
        if (isAuthenticated) {
            checkWishlist();
        }
    }, [productId, isAuthenticated]);

    const fetchProduct = async () => {
        try {
            const response = await axios.get(`${API}/products/${productId}`);
            setProduct(response.data);
        } catch (error) {
            console.error('Error fetching product:', error);
            toast.error('Failed to load product');
        } finally {
            setLoading(false);
        }
    };

    const checkWishlist = async () => {
        try {
            const response = await axios.get(`${API}/wishlist`);
            setIsInWishlist(response.data.products.some(p => p.id === productId));
        } catch (error) {
            console.error('Error checking wishlist:', error);
        }
    };

    const handleAddToCart = async () => {
        try {
            await axios.post(`${API}/cart`, { product_id: productId });
            toast.success('Added to cart!');
        } catch (error) {
            toast.error('Failed to add to cart');
        }
    };

    const handleToggleWishlist = async () => {
        try {
            if (isInWishlist) {
                await axios.delete(`${API}/wishlist/${productId}`);
                setIsInWishlist(false);
                toast.success('Removed from wishlist');
            } else {
                await axios.post(`${API}/wishlist/${productId}`);
                setIsInWishlist(true);
                toast.success('Added to wishlist!');
            }
        } catch (error) {
            toast.error('Failed to update wishlist');
        }
    };

    const handlePlayAudio = () => {
        if (product) {
            setCurrentAudio({
                url: product.audio_url,
                name: product.name
            });
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'sample_pack':
                return 'SAMPLE PACK';
            case 'song':
                return 'SONG';
            case 'album':
                return 'ALBUM';
            default:
                return type.toUpperCase();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-center">
                    <p className="text-xl text-muted-foreground mb-6">Product not found</p>
                    <button onClick={() => navigate('/browse')} className="btn-primary">
                        Browse Products
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="product-detail-page">
            <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
                data-testid="back-button"
            >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm uppercase tracking-widest">Back</span>
            </button>

            <div className="grid lg:grid-cols-2 gap-12">
                {/* Product Image & Player */}
                <div className="sticky top-24 self-start">
                    <div className="relative aspect-square overflow-hidden mb-6">
                        <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute top-6 left-6">
                            <span className="px-4 py-2 bg-black/80 text-xs font-mono uppercase tracking-wider">
                                {getTypeLabel(product.type)}
                            </span>
                        </div>
                    </div>

                    <button 
                        onClick={handlePlayAudio}
                        className="btn-primary w-full flex items-center justify-center gap-3 mb-4"
                        data-testid="play-preview-button"
                    >
                        <Play className="w-5 h-5" />
                        Play Preview
                    </button>
                </div>

                {/* Product Details */}
                <div>
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4" data-testid="product-name">
                        {product.name}
                    </h1>
                    
                    <p className="text-xl text-muted-foreground mb-8" data-testid="product-description">
                        {product.description}
                    </p>

                    <div className="flex items-center gap-6 mb-8">
                        <span className="text-5xl font-bold text-primary" data-testid="product-price">
                            ${product.price}
                        </span>
                    </div>

                    {isAuthenticated ? (
                        <div className="flex gap-4 mb-8">
                            <button 
                                onClick={handleAddToCart}
                                className="btn-primary flex-1 flex items-center justify-center gap-3"
                                data-testid="add-to-cart-button"
                            >
                                <ShoppingCart className="w-5 h-5" />
                                Add to Cart
                            </button>
                            <button 
                                onClick={handleToggleWishlist}
                                className={`p-4 border transition-colors ${isInWishlist ? 'border-primary bg-primary text-white' : 'border-white/20 hover:border-white/40'}`}
                                data-testid="wishlist-toggle-button"
                            >
                                <Heart className="w-6 h-6" fill={isInWishlist ? 'currentColor' : 'none'} />
                            </button>
                        </div>
                    ) : (
                        <div className="bg-muted/30 border border-border p-6 mb-8">
                            <p className="text-muted-foreground mb-4">Sign in to purchase this product</p>
                            <button 
                                onClick={() => navigate('/')}
                                className="btn-primary"
                            >
                                Login / Sign Up
                            </button>
                        </div>
                    )}

                    <div className="border-t border-border pt-8">
                        <h3 className="text-lg font-semibold mb-4">Product Information</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Type:</span>
                                <span>{getTypeLabel(product.type)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Format:</span>
                                <span>Instant Digital Download</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">License:</span>
                                <span>Royalty-Free</span>
                            </div>
                        </div>
                    </div>

                    {/* Reviews Section */}
                    <ProductReviews productId={productId} />
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
